import { LatLngExpression } from 'leaflet';
import * as turf from '@turf/turf';
import booleanIntersects from '@turf/boolean-intersects';

import {
  useAppContext,
  useMapContext,
  useOverlayContext,
  useActiveHexContext,
} from 'shared/context';
import {
  pixelToLatLng,
  latLngToCoords,
  svgPathToPixel,
  coordsToGeoJSON,
  geoJSONToCoords,
} from 'shared/utils';

const RELEVANT_SHAPES = ['Polygon', 'MultiPolygon'];

export const useHexAnalysis = () => {
  const { map } = useMapContext();
  const { setIsBigHexView } = useAppContext();
  const { ctx, paths, layer } = useOverlayContext();
  const { setFeatures, setSqmtArea } = useActiveHexContext();

  const getHexAnalysis = async (path: string) => {
    const hexFeatures = await getFeaturesInHexPath(path);
    const hexSqmtArea = getHexSqmtArea(path);
    setIsBigHexView(true);
    setFeatures(hexFeatures);
    setSqmtArea(hexSqmtArea);
  };

  const getHexToGeoJSON = (path?: string) => {
    if (path) {
      const pathCoords = svgPathToPixel(path);
      const hexLatLng = pixelToLatLng(pathCoords, map);
      const hexCoords = latLngToCoords(hexLatLng);
      const hexGeoJson = coordsToGeoJSON(hexCoords);
      return hexGeoJson;
    }
  };

  const getPathOfHex = (mouseX: number, mouseY: number) => {
    if (ctx) {
      const hexPath = paths.find(path => {
        const hex = new Path2D(path);
        return ctx.isPointInPath(hex, mouseX, mouseY);
      });
      return hexPath;
    }
  };

  const getHexSqmtArea = (path: string) => {
    const hexGeoJSON = getHexToGeoJSON(path);
    if (hexGeoJSON) {
      const hexTurf = turf.polygon([hexGeoJSON]);
      const hexArea = turf.area(hexTurf);
      return hexArea;
    }
    return null;
  };

  const getHexCenterCoords = (path: string): LatLngExpression | null => {
    const hexGeoJSON = getHexToGeoJSON(path);
    if (hexGeoJSON) {
      const hexCenterPoints = turf.points(hexGeoJSON);
      const turfFeature = turf.center(hexCenterPoints);
      const hexCenterCoords = geoJSONToCoords([
        turfFeature.geometry.coordinates,
      ]);
      if (hexCenterCoords?.[0]) {
        return {
          lat: hexCenterCoords[0][0],
          lng: hexCenterCoords[0][1],
        };
      }
    }
    return null;
  };

  const getFeaturesInHexPath = async (hexPath?: string) => {
    const hexGeoJSON = getHexToGeoJSON(hexPath) as turf.Position[];
    const hexTurf = turf.polygon([hexGeoJSON]);
    const allFeaturesInViewport = await layer.scene.queryFeatures({
      geometry: true,
      unique: true,
      // visible: true,
    });
    const featuresInHex = allFeaturesInViewport.filter(
      (feature: turf.helpers.Feature<any, turf.helpers.Properties>) =>
        booleanIntersects(hexTurf, feature) &&
        RELEVANT_SHAPES.includes(feature.geometry.type),
    );
    const intersectedFeatures = featuresInHex
      .map((feature: turf.helpers.Feature<any, turf.helpers.Properties>) => {
        const intersectFeature = turf.intersect(hexTurf, feature);
        if (intersectFeature) {
          const turfArea = turf.area(intersectFeature) ?? 0;
          return {
            ...feature,
            geometry: intersectFeature.geometry,
            area: turfArea,
          };
        }
        return null;
      })
      .filter(Boolean);
    const groupedFeaturesInHex = intersectedFeatures.reduce(
      (
        features: any,
        feature: { area: number; geometry: turf.helpers.GeometryObject },
      ) => {
        // @ts-ignore
        const { kind, kind_detail } = feature.properties;
        const key = kind_detail ?? kind;
        if (!features[key]) {
          features[key] = 0;
        }
        features[key] += feature.area;
        return features;
      },
      {},
    );
    return groupedFeaturesInHex;
  };

  return {
    getHexAnalysis,
    getHexToGeoJSON,
    getPathOfHex,
    getHexSqmtArea,
    getHexCenterCoords,
    getFeaturesInHexPath,
  };
};
